// Backend-driven Flash Exam API client.
//
// Thin wrapper around the `/api/exams*` contract (see exams-backend-api-contract.md).
// Every call is authenticated (Firebase ID token via authApi/authFetch) and unwraps the
// standard `{ success, data }` envelope through parseApiEnvelopeResponse. Create + answer
// are sent through authApiIdempotent so a network retry never double-charges / double-records.
//
// This module is pure (no React). Both orchestrators (AppHome + MainApp) reach the backend
// exclusively through here, via the useExams hook.

import { ENV } from '../config/env.js';
import {
    authApi,
    authApiIdempotent,
    authFetch,
    parseApiEnvelopeResponse,
    createIdempotencyKey,
    ApiError,
} from '../firebase/config.js';

const API_BASE_URL = ENV.apiBaseUrl;
const EXAMS_BASE = `${API_BASE_URL}/api/exams`;

const JSON_HEADERS = { 'Content-Type': 'application/json' };

/**
 * Create an exam. The backend resolves `source`, generates questions, debits creditsBalance
 * (1 credit per produced question), stores the internal definition, and returns the SANITIZED
 * exam (no isCorrect / no per-option explanation) with `examId`.
 *
 * @param {object} source  Discriminated union: { type: 'cards', cards } | { type: 'folders', folderIds } | { type: 'clinical', text, clinical }
 * @param {object} config  { questionCount?, explanationMode? }
 * @param {object} [opts]
 * @param {string} [opts.idempotencyKey]  Reuse a key across retries of the same create.
 * @returns {Promise<object>} sanitized exam: { id, examId, title, config, questions: [{ id, questionText, options: [{ id, text }] }] }
 */
export async function createExam(source, config, { idempotencyKey } = {}) {
    return authApiIdempotent(
        EXAMS_BASE,
        {
            method: 'POST',
            headers: { ...JSON_HEADERS },
            body: JSON.stringify({ source, config }),
        },
        { idempotencyKey: idempotencyKey || createIdempotencyKey() }
    );
}

/**
 * Streaming create. Emits the same SSE event shape as the legacy /create-exam-stream
 * (`title` / `question` / `complete`), each `data:` line carrying the standard envelope.
 *
 * NOTE: streamed creates are NOT covered by the idempotency cache — a retried streaming
 * create re-generates and re-charges. Prefer createExam() when the idempotency guarantee
 * matters. The exam is fully persisted before streaming begins either way.
 *
 * @param {object} source
 * @param {object} config
 * @param {object} handlers  { onTitle(title), onQuestion(question), onComplete({ examId, title, questionCount }) }
 * @param {AbortSignal} [signal]
 */
export async function createExamStream(source, config, handlers = {}, signal) {
    const url = EXAMS_BASE;
    const res = await authFetch(url, {
        method: 'POST',
        headers: { ...JSON_HEADERS, Accept: 'text/event-stream' },
        body: JSON.stringify({ source, config }),
        signal,
    });

    if (!res.ok) {
        // Non-2xx streams still carry a JSON envelope — surface its error.
        await parseApiEnvelopeResponse(res, { url });
        throw new ApiError({
            code: 'INVALID_API_RESPONSE',
            message: 'Invalid API response',
            requestId: res.headers?.get?.('x-request-id') || null,
            status: res.status,
            url,
        });
    }
    if (!res.body) {
        throw new ApiError({
            code: 'INVALID_API_RESPONSE',
            message: 'Invalid API response',
            requestId: res.headers?.get?.('x-request-id') || null,
            status: res.status,
            url,
        });
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const commit = (evt, jsonStr) => {
        const envelope = JSON.parse(jsonStr);
        const requestId =
            (typeof envelope?.error?.requestId === 'string' && envelope.error.requestId) ||
            res.headers?.get?.('x-request-id') ||
            null;
        if (!envelope || typeof envelope !== 'object' || typeof envelope.success !== 'boolean') {
            throw new ApiError({ code: 'INVALID_API_RESPONSE', message: 'Invalid API response', requestId, status: res.status, url });
        }
        if (envelope.success !== true) {
            throw new ApiError({
                code: envelope?.error?.code || 'UNKNOWN_ERROR',
                message: envelope?.error?.message || 'Unknown error',
                requestId,
                details: envelope?.error?.details,
                status: res.status,
                url,
            });
        }
        const payload = envelope.data;
        if (evt === 'title') handlers.onTitle?.(payload.title);
        else if (evt === 'question') handlers.onQuestion?.(payload.question);
        else if (evt === 'complete') handlers.onComplete?.(payload);
    };

    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
            const rawEvent = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            const lines = rawEvent.split('\n');
            let evt = 'message';
            let data = '';
            for (const line of lines) {
                if (line.startsWith('event: ')) evt = line.slice(7).trim();
                else if (line.startsWith('data: ')) data += line.slice(6);
            }
            if (data) commit(evt, data);
        }
    }
}

/** Fetch one sanitized exam (resume / retake). Never includes the answer key. */
export async function getExam(examId) {
    return authApi(`${EXAMS_BASE}/${encodeURIComponent(examId)}`, { method: 'GET' });
}

/** List the user's saved exams → { exams: [{ id, title, createdAt, config }], count }. */
export async function listExams() {
    return authApi(EXAMS_BASE, { method: 'GET' });
}

/**
 * Start an attempt. Returns the server-fixed shuffle already applied:
 * { attemptId, questionOrder, optionOrder, sanitizedQuestions }.
 * `sanitizedQuestions` is already in render order — use it directly.
 */
export async function startAttempt(examId) {
    return authApi(`${EXAMS_BASE}/${encodeURIComponent(examId)}/attempts`, {
        method: 'POST',
        headers: { ...JSON_HEADERS },
        body: JSON.stringify({}),
    });
}

/**
 * Submit one answer; server grades.
 * - explanationMode "now"  → { isCorrect, correctOptionId, explanations, runningScore }
 * - explanationMode "later" → { answered: true } only (nothing revealed).
 * Idempotent per (attempt, question) so a retry of the same answer won't 409 spuriously.
 */
export async function submitAnswer(examId, attemptId, { questionId, optionId }, { idempotencyKey } = {}) {
    return authApiIdempotent(
        `${EXAMS_BASE}/${encodeURIComponent(examId)}/attempts/${encodeURIComponent(attemptId)}/answer`,
        {
            method: 'POST',
            headers: { ...JSON_HEADERS },
            body: JSON.stringify({ questionId, optionId }),
        },
        { idempotencyKey: idempotencyKey || createIdempotencyKey() }
    );
}

/**
 * Finalize an attempt. Returns score + the full answer key for the review screen:
 * { score, totalQuestions, percentage, answerKey: [{ questionId, correctOptionId, sourceCardIds, options:[{ id, text, explanation }] }] }.
 * Idempotent — finishing an already-finished attempt returns the same score.
 */
export async function finishAttempt(examId, attemptId, { flagged = [] } = {}) {
    return authApi(
        `${EXAMS_BASE}/${encodeURIComponent(examId)}/attempts/${encodeURIComponent(attemptId)}/finish`,
        {
            method: 'POST',
            headers: { ...JSON_HEADERS },
            body: JSON.stringify({ flagged }),
        }
    );
}

/** Attempt history (summaries) → { attempts: [...], count }. */
export async function listAttempts(examId) {
    return authApi(`${EXAMS_BASE}/${encodeURIComponent(examId)}/attempts`, { method: 'GET' });
}

/**
 * Report a question as inaccurate. Removes it from the exam and returns a credit refund.
 * reason: 'incorrect_answer' | 'ambiguous' | 'factual_error' | 'mismatch' | 'other'
 * Returns { ok, creditsAwarded, alreadyReported? }
 */
export async function reportQuestion(examId, questionId, { reason }) {
    return authApi(
        `${EXAMS_BASE}/${encodeURIComponent(examId)}/questions/${encodeURIComponent(questionId)}/report`,
        {
            method: 'POST',
            headers: { ...JSON_HEADERS },
            body: JSON.stringify({ reason }),
        }
    );
}
